import { useMemo, useEffect, useRef } from 'react';
import { useWatch, useFormContext } from '@kanda-libs/ks-component-ts';
import type { FinanceRate } from '@kanda-libs/ks-frontend-services';

import { useCurrentJob } from '../../hooks';
import { constructFinancePlan, getFinanceOptionDetails } from '../../utils';

const ContainerComponent = ({ children }) => {
  const { job, isLoading } = useCurrentJob();

  const plan = useWatch({
    name: 'plan',
  });

  const { setValue } = useFormContext();

  const price = useMemo(() => {
    if (!job) return 0;
    return job?.total?.amount_vat_inclusive?.amount || 0;
  }, [job]);

  const depositTaken = useMemo(() => {
    if (!job) return null;
    return job?.deposit_value?.amount || null;
  }, [job]);

  const availableRates = useMemo(() => {
    if (!job) return [];
    return (
      job?.finance_options?.map((option: FinanceRate) => option.name) || []
    );
  }, [job]);

  const deposit = plan?.deposit || 0;

  const builderProps = {
    price,
    availableRates,
    isLoading,
    depositTaken,
  };

  const planProps = {
    total: price,
    deposit,
    plan: constructFinancePlan(plan),
  };

  const initialSet = useRef(false);
  useEffect(() => {
    if (initialSet.current) return;
    if (!availableRates) return;
    if (availableRates?.length !== 1) {
      initialSet.current = true;
      return;
    }
    const { interest, duration } = getFinanceOptionDetails(availableRates[0]);
    const depositValue =
      deposit ||
      job?.deposit_value?.amount ||
      Math.floor(0.1 * (job?.total?.amount_vat_inclusive?.amount || 0));
    const initialPlan = {
      deposit: depositValue,
      rate: String(interest),
      term: String(duration),
    };
    setTimeout(() => {
      setValue('plan', initialPlan);
    }, 10);

    initialSet.current = true;
  }, [availableRates, setValue, deposit, job]);

  return children({
    builderProps,
    planProps,
  });
};

ContainerComponent.displayName = 'FinancePlanBuilder-container';

export default ContainerComponent;
